import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui-components/ui/tabs";
import { 
  Loader2, 
  Image as ImageIcon,
  Search,
  Upload,
  X,
  CheckCircle2
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useBrowserUpload } from "../hooks/useBrowserUpload";
import { FileMetadata } from "../components/FileDropZone";
import { 
  useMediaLibraryListQuery, 
  useMediaLibraryUpload, 
  useMediaLibraryGetCategories 
} from "@/lib/api/requests/media-library.requests";
import { useOrganizationsCustomerFindOneQuery } from "@/lib/api/requests/organizations.requests";
import { MediaLibraryItemDto } from "@/lib/api/interfaces/media-library.interface";
import { CategorySelector } from './CategorySelector';
import { usePermissions } from "@/lib/hooks/use-permissions";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface AddImageDialogProps {
  /**
   * The trigger element that opens the dialog
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open (controlled mode)
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes (controlled mode)
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Callback function called after successful image selection/upload
   */
  onImageSelected: (path: string, id: string | null, displayName: string, width: number, height: number, previewUrl?: string, relativePath?: string) => void;
  /**
   * Optional list of allowed MIME types. If provided, filters library and restricts upload.
   */
  allowedMimeTypes?: string[];
}

const DEFAULT_PAGE_SIZE = 8;

/**
 * Dialog component for adding images to templates
 * Allows users to select from media library or upload new images
 */
/**
 * Debounces a changing value to reduce rapid updates.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}

export const AddImageDialog: React.FC<AddImageDialogProps> = ({
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onImageSelected,
  allowedMimeTypes,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("library");
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaLibraryItemDto | null>(null);
  const [selectedOrgLogoIndex, setSelectedOrgLogoIndex] = useState<number | null>(null);
  const [uploadedImage, setUploadedImage] = useState<FileMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Debounce search input to avoid excessive queries
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const { isInternal, can } = usePermissions();

  // Compute allowed mime types based on permission
  const effectiveAllowedMimeTypes = useMemo(() => {
    const baseTypes = allowedMimeTypes || [
      "image/jpeg", 
      "image/png", 
      "image/gif", 
      "image/webp", 
      "image/heic", 
      "image/heif"
    ];
    
    // Check if SVG is in baseTypes or should be added
    const hasSvg = baseTypes.includes("image/svg+xml");
    
    if (can('features.svg_support')) {
      if (!allowedMimeTypes && !hasSvg) {
        return [...baseTypes, "image/svg+xml"];
      }
      return baseTypes;
    } else {
      // Filter out SVG if feature disabled
      return baseTypes.filter(t => t !== "image/svg+xml");
    }
  }, [allowedMimeTypes, can]);

  // Fetch organization data for logos
  const { data: org } = useOrganizationsCustomerFindOneQuery({ enabled: !isInternal });

  // Category + pagination state (backend-driven)
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [allMedia, setAllMedia] = useState<MediaLibraryItemDto[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');
  const currentFilterKey = useMemo(
    () => `${debouncedSearchQuery.trim()}|${selectedCategory}`,
    [debouncedSearchQuery, selectedCategory]
  );

  // Fetch categories for dropdown (enable when dialog is open)
  const categoriesQuery = useMediaLibraryGetCategories({ enabled: open });
  const categories = useMemo(() => {
    const apiCategories = categoriesQuery.data?.categories || [];
    return ['all', ...apiCategories];
  }, [categoriesQuery.data]);

  // Fetch media library items (backend pagination + search + sorting)
  const { 
    data: mediaResponse, 
    isLoading: isLoadingMedia, 
    error: mediaError, 
    refetch: refetchMedia 
  } = useMediaLibraryListQuery(
    { page: currentPage, limit: DEFAULT_PAGE_SIZE },
    {
      textSearch: debouncedSearchQuery.trim() || undefined,
      categories: selectedCategory === 'all' ? undefined : [selectedCategory],
      sortBy: 'updated',
      direction: 'desc',
      mimeType: effectiveAllowedMimeTypes
    }
  );

  // Reset pagination when search/filter changes
  React.useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllMedia([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  // Append page results to aggregated list
  React.useEffect(() => {
    if (mediaResponse?.docs) {
      if (currentPage === 1) {
        setAllMedia(mediaResponse.docs);
      } else {
        setAllMedia(prev => [...prev, ...mediaResponse.docs]);
      }
    }
  }, [mediaResponse?.docs, currentPage]);

  const hasMoreItems = mediaResponse?.hasNextPage ?? false;
  const isInitialLoading = isLoadingMedia && currentPage === 1;
  const isLoadingMore = isLoadingMedia && currentPage > 1;

  const handleLoadMore = useCallback(() => {
    if (hasMoreItems && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMoreItems, isLoadingMore]);

  // Media upload mutation
  const uploadMutation = useMediaLibraryUpload({
    onSuccess: (data) => {
      toast.success("Bild erfolgreich hochgeladen", {
        description: `${data.displayName} wurde zur Medienbibliothek hinzugefügt`,
      });
      refetchMedia();
      setUploadedImage(null);
      setSelectedMediaItem(data);
      setSelectedOrgLogoIndex(null);
      setSelectedTab("library");
    },
    onError: (error) => {
      toast.error("Upload fehlgeschlagen", {
        description: error.message || "Ein unbekannter Fehler ist aufgetreten",
        error,
      });
    },
  });

  // Browser upload hook configuration (single file only)
  const {
    openFileDialog,
    isProcessing: isBrowserUploadProcessing,
    error: browserUploadError
  } = useBrowserUpload({
    acceptedFileTypes: allowedMimeTypes || [
      "image/jpeg", 
      "image/png", 
      "image/gif", 
      "image/webp", 
      "image/heic", 
      "image/heif",
      "image/svg+xml"
    ],
    maxFiles: 1,
    maxFileSize: 10 * 1024 * 1024,
    onFilesReady: (files) => {
      if (files.length > 0) {
        setUploadedImage(files[0]);
        setSelectedMediaItem(null);
        setSelectedOrgLogoIndex(null);
        setSelectedTab("upload");
      }
    },
    onError: (errorMessage) => {
      toast.error('Dateiauswahl fehlgeschlagen', {
        description: errorMessage,
        duration: 5000,
        });
    }
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedTab("library");
      setSelectedMediaItem(null);
      setSelectedOrgLogoIndex(null);
      setUploadedImage(null);
      setSearchQuery("");
      setSelectedCategory('all');
      setCurrentPage(1);
      setAllMedia([]);
      setLastFilterKey('');
    } else {
      refetchMedia();
    }
  }, [open, refetchMedia]);

  // Handle browser upload error display
  useEffect(() => {
    if (browserUploadError) {
      toast.error('Upload-Fehler', {
        description: browserUploadError,
        duration: 5000,
      });
    }
  }, [browserUploadError]);

  /**
   * Handle image selection from media library
   */
  const handleLibraryImageSelect = (item: MediaLibraryItemDto) => {
    setSelectedMediaItem(item);
    setSelectedOrgLogoIndex(null);
    setUploadedImage(null);
  };

  /**
   * Handle organization logo selection
   */
  const handleOrgLogoSelect = (index: number) => {
    setSelectedOrgLogoIndex(index);
    setSelectedMediaItem(null);
    setUploadedImage(null);
  };

  /**
   * Handle final image confirmation
   */
  /**
   * Handle final image confirmation
   */
  const handleConfirm = async () => {
    setIsLoading(true);
    
    try {
      let finalImage: { mediaId: string | null; url: string; displayName: string; relativePath?: string } | null = null;

      // Handle library selection
      if (selectedTab === "library" && selectedMediaItem) {
        finalImage = {
          mediaId: selectedMediaItem.mediaId,
          url: selectedMediaItem.url,
          displayName: selectedMediaItem.displayName,
        };
      } 
      // Handle org logo selection
      else if (selectedTab === "library" && selectedOrgLogoIndex !== null && org?.logoUrls && org.logos) {
        const logoUrl = org.logoUrls[selectedOrgLogoIndex];
        const logoDetails = org.logos[selectedOrgLogoIndex];
        
        if (logoUrl && logoDetails) {
            finalImage = {
            mediaId: null, // Org logos don't have a media library ID
            url: logoUrl, // This is a relative path like "organizations/..." or "assets/..."
            displayName: "Vereinswappen",
            relativePath: logoDetails.path,
            };
        }
      }
      // Handle upload
      else if (selectedTab === "upload" && uploadedImage) {
        if (!uploadedImage.file) {
          throw new Error("Keine Datei zum Hochladen gefunden.");
        }
        const uploadedResponse = await uploadMutation.mutateAsync({ file: uploadedImage.file });
        finalImage = {
          mediaId: uploadedResponse.mediaId,
          url: uploadedResponse.url,
          displayName: uploadedResponse.displayName,
        };
      }

      if (finalImage) {
        // Load image to get its dimensions
        // For SVGs and other formats, we try to get intrinsic dimensions.
        // If it fails (e.g. SVG issues), we fallback to default dimensions to not block the user.
        let width = 100;
        let height = 100;
        const imageToProcess = finalImage;

        try {
          const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              // For SVGs without intrinsic size, width/height might be 0
              if (img.width === 0 || img.height === 0) {
                resolve({ width: 100, height: 100 });
              } else {
                resolve({ width: img.width, height: img.height });
              }
            };
            img.onerror = () => {
              console.warn("Could not determine image dimensions, using defaults.");
              resolve({ width: 100, height: 100 });
            };
            img.src = imageToProcess.url;
          });
          width = dimensions.width;
          height = dimensions.height;
        } catch (e) {
          console.warn("Image dimension check failed", e);
        }

        onImageSelected(finalImage.url, finalImage.mediaId, finalImage.displayName, width, height, undefined, finalImage.relativePath);
        toast.success('Bild erfolgreich ausgewählt!', {
          duration: 3000,
        });
        setOpen(false);
      } else {
        throw new Error("Kein Bild ausgewählt oder hochgeladen");
      }
    } catch (error) {
      console.error('Fehler bei der Bildverarbeitung:', error);
      toast.error('Fehler', {
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        duration: 5000,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if confirm button should be enabled
  const isConfirmEnabled = (selectedMediaItem !== null && selectedTab === "library") || 
                          (selectedOrgLogoIndex !== null && selectedTab === "library") ||
                          (uploadedImage !== null && selectedTab === "upload");

  return (
    <>
      {trigger && (
        <div onClick={() => setOpen(true)} className="inline-block">
          {trigger}
        </div>
      )}
      <CreateBaseDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={ImageIcon}
        title="Bild hinzufügen"
        description="Wähle ein Bild aus der Medienbibliothek oder lade ein neues hoch."
        hideSubmitButton={true}
        maxWidth="xl"
        customFooterActions={
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || isBrowserUploadProcessing || uploadMutation.isPending || !isConfirmEnabled}
            className="flex-1 sm:flex-none h-11 px-8 bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 transition-all min-w-[140px] font-bold rounded-xl"
          >
            {isLoading || uploadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verarbeiten...
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4 mr-2 hidden sm:inline-block" />
                Hinzufügen
              </>
            )}
          </Button>
        }
      >
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6 pt-4 bg-gray-50/30">
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full flex flex-col min-h-min">
              <TabsList className="flex w-full mb-5 bg-gray-100/80 p-1.5 rounded-xl shrink-0 border border-gray-200/50">
                <TabsTrigger
                  value="library"
                  className="flex-1 p-1 text-xs sm:text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 text-gray-500 font-bold transition-all"
                >
                  Medienbibliothek
                </TabsTrigger>
                <TabsTrigger
                  value="upload"
                  className="flex-1 p-1  text-xs sm:text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 text-gray-500 font-bold transition-all"
                >
                  Neu hochladen
                </TabsTrigger>
              </TabsList>

              {/* Medienbibliothek Tab */}
              <TabsContent value="library" className="flex flex-col gap-5 m-0 data-[state=inactive]:hidden w-full">
              
              {/* Organization Logos Section */}
              {org?.logoUrls && org.logoUrls.length > 0 && (
                <div className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vereinswappen</h3>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {org.logoUrls.map((url, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleOrgLogoSelect(i)}
                        className={`
                          relative aspect-square rounded-lg overflow-hidden border transition-all duration-200 bg-white p-1 flex items-center justify-center
                          ${selectedOrgLogoIndex === i
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-400 hover:ring-1 hover:ring-blue-100 hover:shadow-sm'
                          }
                        `}
                      >
                        <img
                          src={url}
                          crossOrigin="anonymous"
                          alt="Vereinswappen"
                          className="max-w-full max-h-full object-contain"
                        />
                        {selectedOrgLogoIndex === i && (
                          <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center shadow-sm">
                            <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Filter bar: Search + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Bilder suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-sm bg-gray-100 border-0 focus:bg-gray-100 focus:ring-0 focus:outline-none rounded-lg transition-colors"
                  />
                </div>

                <CategorySelector
                  categories={categories.slice(1)} // Remove 'all' from list
                  selected={selectedCategory === 'all' ? [] : [selectedCategory]}
                  onChange={(cats) => setSelectedCategory(cats[0] || 'all')}
                  isLoading={false}
                  mode="single"
                  allowCreate={false}
                  placeholder="Alle Kategorien"
                />
              </div>

              {isInitialLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-500">Lade Medienbibliothek...</span>
                </div>
              ) : mediaError ? (
                <div className="flex items-center justify-center h-40 text-red-500">
                  Fehler beim Laden der Medienbibliothek.
                </div>
              ) : allMedia.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-500">
                  Keine Bilder gefunden.
                </div>
              ) : (
                <>
                  <div className="w-full pb-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {allMedia.map((item) => (
                        <button
                          key={item.mediaId}
                          type="button"
                          onClick={() => handleLibraryImageSelect(item)}
                          className={`
                            relative rounded-xl overflow-hidden border transition-all duration-200 aspect-[4/3] flex flex-col items-center justify-center bg-white group
                            ${selectedMediaItem?.mediaId === item.mediaId
                              ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/30 shadow-md' 
                              : 'border-gray-200 hover:border-blue-400 hover:ring-1 hover:ring-blue-100 hover:shadow-sm'
                            }
                          `}
                        >
                          <img
                            src={item.thumbnailUrl || item.url}
                            alt={item.displayName}
                            crossOrigin="anonymous"
                            className={`w-full h-full ${item.mimeType === 'image/svg+xml' ? 'object-contain p-4' : 'object-cover'}`}
                            loading="lazy"
                          />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                            <span className="text-white font-medium text-[11px] leading-tight truncate w-full text-left">{item.displayName}</span>
                          </div>
                          {selectedMediaItem?.mediaId === item.mediaId && (
                            <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center shadow-sm z-10">
                              <CheckCircle2 className="h-3 w-3 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Load more */}
                  {hasMoreItems && (
                    <div className="flex items-center justify-center">
                      <Button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        variant="outline"
                        className="h-9 px-4 rounded-md bg-white border-gray-200 hover:bg-gray-50 text-sm"
                      >
                        {isLoadingMore ? 'Lädt...' : 'Mehr laden'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            
            {/* Upload Tab */}
            <TabsContent value="upload" className="flex flex-col gap-5 m-0 data-[state=inactive]:hidden pb-8">
              {!uploadedImage ? (
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center bg-white hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer shadow-sm" onClick={openFileDialog}>
                  <div className="h-14 w-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-sm">
                    <Upload className="h-6 w-6 text-gray-400" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Eigenes Bild hochladen</h4>
                  <p className="text-xs text-gray-500 mb-5 max-w-[250px] mx-auto leading-relaxed">
                    Ziehe ein Bild hierher oder klicke, um deine Dateien zu durchsuchen.<br/><br/>
                    JPG, PNG, WEBP, HEIC{can('features.svg_support') ? ', SVG' : ''} (max. 10MB)
                  </p>
                  <Button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openFileDialog(); }}
                    disabled={isBrowserUploadProcessing || uploadMutation.isPending}
                    className="h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 transition-all font-bold text-sm w-full sm:w-auto rounded-xl"
                  >
                    {isBrowserUploadProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Verarbeitung...
                      </>
                    ) : (
                      "Datei auswählen"
                    )}
                  </Button>
                </div>
              ) : null}
              
              {uploadedImage && (
                <div className="border border-green-200 bg-green-50/50 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3 border-b border-green-100 pb-3">
                    <span className="text-sm font-bold text-green-900 truncate max-w-[200px] flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      {uploadedImage.displayName}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setUploadedImage(null)}
                      className="h-8 w-8 p-0 text-green-700 hover:bg-green-100 hover:text-green-900 rounded-lg transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-white border border-green-100 shadow-inner flex items-center justify-center p-2">
                    {uploadedImage.preview && (
                      <img 
                        src={uploadedImage.preview} 
                        alt={uploadedImage.displayName}
                        className="max-w-full max-h-full object-contain rounded-md"
                      />
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
            </Tabs>
        </div>
      </CreateBaseDialog>
    </>
  );
};
